export type OrcRichTextRenderContext = {
  level?: number;
  actor?: {
    level?: number;
    system?: Record<string, unknown>;
  };
  item?: {
    rank?: number;
    level?: number;
    system?: Record<string, unknown>;
  } & Record<string, unknown>;
};

export type RichTextToken =
  | { kind: 'text'; value: string }
  | { kind: 'orc'; raw: string; type: string; id: string; label?: string }
  | { kind: 'damage'; raw: string; payload: string; label?: string }
  | { kind: 'check'; raw: string; payload: string; label?: string }
  | { kind: 'template'; raw: string; payload: string; label?: string }
  | { kind: 'inlineRoll'; raw: string; command: string; label?: string };

type RenderOptions = {
  html?: boolean;
  inline?: boolean;
  context?: OrcRichTextRenderContext;
  resolveOrcLabel: (type: string, id: string, label?: string) => string;
  translateRuleLabel?: (
    key: string,
    fallback: string,
    params?: Record<string, string | number>,
  ) => string;
  translateRuleValue?: (
    kind: 'skill' | 'save' | 'damageType' | 'templateType' | 'checkType',
    value: string,
  ) => string;
};

type EvalToken =
  | { type: 'number'; value: number }
  | { type: 'ref'; value: string }
  | { type: 'ident'; value: string }
  | { type: 'op'; value: string };

const INLINE_ROLL_RE = /^\[\[\/(r|gmr|act)\s+([\s\S]*?)\]\]/;
const DICE_RE = /\b\d+d(?:\d+|%)(?:\s*[+-]\s*\d+)?\b/gi;
const DICE_TEXT_RE = /\b\d+d(?:\d+|%)(?:\s*[+-]\s*\d+)?\b/i;
const SUPPORTED_TAGS = new Set(['ORC', 'Damage', 'Check', 'Template']);
const LEADING_CLOSING_PUNCTUATION_RE = /^([,.;:!?…)\]}»”]+)/;
const SAVE_SLUGS = new Set(['fortitude', 'reflex', 'will']);
const SKILL_SLUGS = new Set([
  'acrobatics',
  'arcana',
  'athletics',
  'crafting',
  'deception',
  'diplomacy',
  'intimidation',
  'medicine',
  'nature',
  'occultism',
  'performance',
  'religion',
  'society',
  'stealth',
  'survival',
  'thievery',
]);

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function findBalancedClose(value: string, openIndex: number, open: string, close: string): number {
  let depth = 0;
  for (let i = openIndex; i < value.length; i += 1) {
    const char = value[i];
    if (char === open) depth += 1;
    else if (char === close) {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function readOptionalLabel(value: string, start: number): { label?: string; end: number } {
  if (value[start] !== '{') return { end: start };
  const close = findBalancedClose(value, start, '{', '}');
  if (close === -1) return { end: start };
  return { label: value.slice(start + 1, close), end: close + 1 };
}

function pushText(tokens: RichTextToken[], value: string): void {
  if (!value) return;
  const last = tokens[tokens.length - 1];
  if (last?.kind === 'text') {
    last.value += value;
  } else {
    tokens.push({ kind: 'text', value });
  }
}

export function parseRichTextTokens(content: string): RichTextToken[] {
  const tokens: RichTextToken[] = [];
  let index = 0;

  while (index < content.length) {
    const inlineMatch = content.slice(index).match(INLINE_ROLL_RE);
    if (inlineMatch) {
      const labelInfo = readOptionalLabel(content, index + inlineMatch[0].length);
      const raw = content.slice(index, labelInfo.end);
      tokens.push({
        kind: 'inlineRoll',
        raw,
        command: `/${inlineMatch[1]} ${inlineMatch[2].trim()}`,
        label: labelInfo.label,
      });
      index = labelInfo.end;
      continue;
    }

    if (content[index] === '@') {
      const tagMatch = content.slice(index).match(/^@([A-Za-z][A-Za-z0-9]*)\[/);
      if (tagMatch) {
        const tag = tagMatch[1];
        const openIndex = index + tagMatch[0].length - 1;
        const closeIndex = findBalancedClose(content, openIndex, '[', ']');
        if (closeIndex !== -1) {
          const payload = content.slice(openIndex + 1, closeIndex);
          const labelInfo = readOptionalLabel(content, closeIndex + 1);
          const raw = content.slice(index, labelInfo.end);

          if (SUPPORTED_TAGS.has(tag)) {
            if (tag === 'ORC') {
              const separator = payload.indexOf(':');
              if (separator > 0) {
                tokens.push({
                  kind: 'orc',
                  raw,
                  type: payload.slice(0, separator),
                  id: payload.slice(separator + 1),
                  label: labelInfo.label,
                });
                index = labelInfo.end;
                continue;
              }
            } else if (tag === 'Damage') {
              tokens.push({ kind: 'damage', raw, payload, label: labelInfo.label });
              index = labelInfo.end;
              continue;
            } else if (tag === 'Check') {
              tokens.push({ kind: 'check', raw, payload, label: labelInfo.label });
              index = labelInfo.end;
              continue;
            } else if (tag === 'Template') {
              tokens.push({ kind: 'template', raw, payload, label: labelInfo.label });
              index = labelInfo.end;
              continue;
            }
          }
        }
      }
    }

    const nextSpecial = content.slice(index + 1).search(/@(?:ORC|Damage|Check|Template)\[|\[\[\/(?:r|gmr|act)\s/);
    if (nextSpecial === -1) {
      pushText(tokens, content.slice(index));
      break;
    }
    const nextIndex = index + 1 + nextSpecial;
    pushText(tokens, content.slice(index, nextIndex));
    index = nextIndex;
  }

  return tokens;
}

function tokenizeExpression(expression: string): EvalToken[] | null {
  const tokens: EvalToken[] = [];
  let index = 0;

  while (index < expression.length) {
    const rest = expression.slice(index);
    const space = rest.match(/^\s+/);
    if (space) {
      index += space[0].length;
      continue;
    }

    const number = rest.match(/^\d+(?:\.\d+)?/);
    if (number) {
      tokens.push({ type: 'number', value: Number(number[0]) });
      index += number[0].length;
      continue;
    }

    const ref = rest.match(/^@(actor|item)(?:\.level|\.rank|\.system(?:\.[A-Za-z0-9_-]+)+)/);
    if (ref) {
      tokens.push({ type: 'ref', value: ref[0] });
      index += ref[0].length;
      continue;
    }

    const ident = rest.match(/^[A-Za-z_][A-Za-z0-9_]*/);
    if (ident) {
      tokens.push({ type: 'ident', value: ident[0] });
      index += ident[0].length;
      continue;
    }

    const op = rest[0];
    if ('+-*/(),'.includes(op)) {
      tokens.push({ type: 'op', value: op });
      index += 1;
      continue;
    }

    return null;
  }

  return tokens;
}

function numericValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
}

function contextRefValue(ref: string, context?: OrcRichTextRenderContext): number | null {
  if (ref === '@actor.level') {
    return context?.actor?.level ?? context?.level ?? null;
  }

  if (ref === '@item.rank') {
    return numericValue(context?.item?.rank);
  }

  if (ref === '@item.level') {
    return numericValue(context?.item?.level);
  }

  const systemPrefix = ref.startsWith('@actor.system.')
    ? '@actor.system.'
    : ref.startsWith('@item.system.')
      ? '@item.system.'
      : null;
  if (!systemPrefix) return null;

  let cursor: unknown = systemPrefix === '@actor.system.'
    ? context?.actor?.system
    : context?.item?.system;
  for (const part of ref.slice(systemPrefix.length).split('.')) {
    if (!cursor || typeof cursor !== 'object') return null;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return numericValue(cursor);
}

function evaluateTokens(tokens: EvalToken[], context?: OrcRichTextRenderContext): number | null {
  let position = 0;

  const peek = () => tokens[position];
  const consume = (value?: string) => {
    const token = tokens[position];
    if (!token) return null;
    if (value && (token.type !== 'op' || token.value !== value)) return null;
    position += 1;
    return token;
  };

  const parseExpression = (): number | null => {
    let value = parseTerm();
    if (value === null) return null;
    while (peek()?.type === 'op' && ['+', '-'].includes(String(peek()?.value))) {
      const op = consume()?.value;
      const right = parseTerm();
      if (right === null) return null;
      value = op === '+' ? value + right : value - right;
    }
    return value;
  };

  const parseTerm = (): number | null => {
    let value = parseUnary();
    if (value === null) return null;
    while (peek()?.type === 'op' && ['*', '/'].includes(String(peek()?.value))) {
      const op = consume()?.value;
      const right = parseUnary();
      if (right === null || (op === '/' && right === 0)) return null;
      value = op === '*' ? value * right : value / right;
    }
    return value;
  };

  const parseUnary = (): number | null => {
    if (peek()?.type === 'op' && peek().value === '-') {
      consume('-');
      const value = parseUnary();
      return value === null ? null : -value;
    }
    return parsePrimary();
  };

  const parseArgs = (): number[] | null => {
    if (!consume('(')) return null;
    const args: number[] = [];
    if (peek()?.type === 'op' && peek().value === ')') {
      consume(')');
      return args;
    }
    while (position < tokens.length) {
      const value = parseExpression();
      if (value === null) return null;
      args.push(value);
      if (peek()?.type === 'op' && peek().value === ',') {
        consume(',');
        continue;
      }
      if (peek()?.type === 'op' && peek().value === ')') {
        consume(')');
        return args;
      }
      return null;
    }
    return null;
  };

  const parsePrimary = (): number | null => {
    const token = peek();
    if (!token) return null;
    if (token.type === 'number') {
      position += 1;
      return token.value;
    }
    if (token.type === 'ref') {
      position += 1;
      return contextRefValue(token.value, context);
    }
    if (token.type === 'op' && token.value === '(') {
      consume('(');
      const value = parseExpression();
      if (value === null || !consume(')')) return null;
      return value;
    }
    if (token.type === 'ident') {
      position += 1;
      const name = token.value;
      const args = parseArgs();
      if (!args) return null;
      switch (name) {
        case 'floor': return args.length === 1 ? Math.floor(args[0]) : null;
        case 'ceil': return args.length === 1 ? Math.ceil(args[0]) : null;
        case 'min': return args.length >= 1 ? Math.min(...args) : null;
        case 'max': return args.length >= 1 ? Math.max(...args) : null;
        case 'gte': return args.length === 2 ? Number(args[0] >= args[1]) : null;
        case 'gt': return args.length === 2 ? Number(args[0] > args[1]) : null;
        case 'lte': return args.length === 2 ? Number(args[0] <= args[1]) : null;
        case 'lt': return args.length === 2 ? Number(args[0] < args[1]) : null;
        case 'eq': return args.length === 2 ? Number(args[0] === args[1]) : null;
        case 'ternary': return args.length === 3 ? (args[0] ? args[1] : args[2]) : null;
        default: return null;
      }
    }
    return null;
  };

  const result = parseExpression();
  return result !== null && position === tokens.length && Number.isFinite(result) ? result : null;
}

export function evaluateRichTextFormula(expression: string, context?: OrcRichTextRenderContext): number | null {
  const tokens = tokenizeExpression(expression);
  return tokens ? evaluateTokens(tokens, context) : null;
}

function renderDiceText(value: string, escapeText: boolean): string {
  let result = '';
  let lastIndex = 0;
  for (const match of value.matchAll(DICE_RE)) {
    const raw = match[0];
    const index = match.index ?? 0;
    const chunk = value.slice(lastIndex, index);
    result += escapeText ? escapeHtml(chunk) : chunk;
    result += `<span class="orc-rule-token orc-dice-token" title="${escapeHtml(raw)}">${escapeHtml(raw)}</span>`;
    lastIndex = index + raw.length;
  }
  const rest = value.slice(lastIndex);
  result += escapeText ? escapeHtml(rest) : rest;
  return result;
}

function renderText(value: string, html: boolean, inline: boolean): string {
  const rendered = renderDiceText(value, !html);
  // In HTML mode newlines are insignificant whitespace — block tags (<p>, <hr>, <br>)
  // already control line breaks. Converting \n → <br/> there injected spurious blank
  // lines between paragraphs (e.g. between Heightened entries). Only plain-text/markdown
  // input (html === false) needs \n turned into explicit breaks.
  if (inline || html) return rendered;
  return rendered.replace(/\n/g, '<br />');
}

function translatedRuleValue(
  options: RenderOptions,
  kind: 'skill' | 'save' | 'damageType' | 'templateType' | 'checkType',
  value: string,
): string {
  return options.translateRuleValue?.(kind, value)?.trim() || '';
}

function damageFormulaLabel(
  payload: string,
  options: RenderOptions,
): string {
  const damageTypes = [...payload.matchAll(/\[([^\]]+)\]/g)]
    .flatMap(match => match[1].split(','))
    .map(type => type.trim())
    .filter(Boolean)
    .map(type => translatedRuleValue(options, 'damageType', type) || type);
  const formula = payload.replace(/\[[^\]]+\]/g, '').trim();
  const diceMatch = formula.match(/^(.+?)d(\d+|%)(.*)$/i);
  if (!diceMatch) {
    const flatValue = evaluateRichTextFormula(formula, options.context);
    if (flatValue === null) return payload;
    const normalizedFlat = Math.max(0, Math.floor(flatValue));
    return [
      String(normalizedFlat),
      damageTypes.join(' '),
    ].filter(Boolean).join(' ');
  }

  const countExpr = diceMatch[1].trim();
  const sides = diceMatch[2];
  const suffix = diceMatch[3].trim();
  const count = /^\d+$/.test(countExpr)
    ? Number(countExpr)
    : evaluateRichTextFormula(countExpr, options.context);
  if (count === null) return payload;

  const normalizedCount = Math.max(0, Math.floor(count));
  return [
    `${normalizedCount}d${sides}${suffix}`,
    damageTypes.join(' '),
  ].filter(Boolean).join(' ');
}

function ruleLabel(
  translateRuleLabel: RenderOptions['translateRuleLabel'],
  key: string,
  fallback: string,
  params?: Record<string, string | number>,
): string {
  if (translateRuleLabel) return translateRuleLabel(key, fallback, params);
  return Object.entries(params || {}).reduce(
    (label, [name, value]) => label.split(`{{${name}}}`).join(String(value)),
    fallback,
  );
}

function parseRuleParts(payload: string): { head: string; attrs: string[] } {
  const parts = payload.split('|').map(part => part.trim()).filter(Boolean);
  return { head: parts[0] || payload, attrs: parts.slice(1) };
}

function normalizeRuleSlug(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_]+/g, '-');
}

function humanizeRuleSlug(value: string): string {
  const words = value.trim().replace(/[_-]+/g, ' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : value;
}

export function inlineActionSlug(command: string): string | null {
  const match = command.match(/^\/act\s+([a-z0-9][a-z0-9_-]*)\b/i);
  return match ? normalizeRuleSlug(match[1]) : null;
}

function inlineRollFormula(command: string): string | null {
  if (!/^\/(?:r|gmr)\s+/i.test(command)) return null;
  const formula = command.replace(/^\/(?:r|gmr)\s+/i, '').split(/\s+#/, 1)[0].trim();
  const dice = formula.match(DICE_TEXT_RE)?.[0];
  if (dice) return dice.replace(/\s+/g, '');
  if (/^\d+(?:\.\d+)?$/.test(formula)) return formula;
  return null;
}

function localizedCheckName(head: string, options: RenderOptions): string {
  const slug = normalizeRuleSlug(head);
  if (SAVE_SLUGS.has(slug)) {
    return translatedRuleValue(options, 'save', slug) || humanizeRuleSlug(slug);
  }
  if (SKILL_SLUGS.has(slug)) {
    return translatedRuleValue(options, 'skill', slug) || humanizeRuleSlug(slug);
  }
  return translatedRuleValue(options, 'checkType', slug) || humanizeRuleSlug(slug);
}

function renderRuleToken(token: RichTextToken, options: RenderOptions, trailingPunctuation = ''): string {
  const punctuationSuffix = escapeHtml(trailingPunctuation);
  if (token.kind === 'orc') {
    const safeType = escapeHtml(token.type);
    const safeId = escapeHtml(token.id);
    const label = options.resolveOrcLabel(token.type, token.id, token.label);
    const safeLabel = escapeHtml(label);
    return `<button type="button" class="orc-link-button" data-orc-link="true" data-orc-type="${safeType}" data-orc-id="${safeId}" data-orc-label="${safeLabel}">${safeLabel}${punctuationSuffix}</button>`;
  }

  if (token.kind === 'damage') {
    const label = token.label?.trim() || damageFormulaLabel(token.payload, options);
    const diceClass = DICE_TEXT_RE.test(label) ? ' orc-dice-token' : '';
    return `<span class="orc-rule-token orc-damage-token${diceClass}" title="${escapeHtml(token.raw)}">${escapeHtml(label)}${punctuationSuffix}</span>`;
  }

  if (token.kind === 'check') {
    const { head, attrs } = parseRuleParts(token.payload);
    const prefix = ruleLabel(options.translateRuleLabel, 'check', 'Check');
    const details = attrs.flatMap((attr) => {
      const [rawKey, ...rawValueParts] = attr.split(':');
      const key = normalizeRuleSlug(rawKey);
      const value = rawValueParts.join(':').trim();
      if (key === 'dc' && /^\d+$/.test(value)) {
        return [ruleLabel(options.translateRuleLabel, 'dcValue', 'DC {{dc}}', { dc: value })];
      }
      if (key === 'basic') {
        return [ruleLabel(options.translateRuleLabel, 'basic', 'basic')];
      }
      if (key === 'against' && value) {
        const againstValue = localizedCheckName(value, options);
        return [ruleLabel(
          options.translateRuleLabel,
          'againstValue',
          'against {{value}}',
          { value: againstValue },
        )];
      }
      return [];
    });
    const checkSuffix = details.length ? ` (${details.join(', ')})` : '';
    const label = token.label?.trim() || `${prefix}: ${localizedCheckName(head, options)}${checkSuffix}`;
    return `<span class="orc-rule-token orc-check-token" title="${escapeHtml(token.raw)}">${escapeHtml(label)}${punctuationSuffix}</span>`;
  }

  if (token.kind === 'template') {
    const { head, attrs } = parseRuleParts(token.payload);
    const typeAttr = head.startsWith('type:') ? head.slice('type:'.length) : head;
    const distance = attrs.find(attr => attr.startsWith('distance:'))?.slice('distance:'.length);
    const templateName = translatedRuleValue(options, 'templateType', typeAttr)
      || ruleLabel(options.translateRuleLabel, `template.${normalizeRuleSlug(typeAttr)}`, humanizeRuleSlug(typeAttr));
    const label = token.label?.trim() || (distance
      ? ruleLabel(
        options.translateRuleLabel,
        'templateWithDistance',
        '{{distance}}-foot {{shape}}',
        { distance, shape: templateName },
      )
      : templateName);
    return `<span class="orc-rule-token orc-template-token" title="${escapeHtml(token.raw)}">${escapeHtml(label)}${punctuationSuffix}</span>`;
  }

  if (token.kind === 'inlineRoll') {
    const actionSlug = inlineActionSlug(token.command);
    const resolvedAction = actionSlug
      ? options.resolveOrcLabel('action', actionSlug)
      : '';
    const actionPlaceholder = actionSlug ? `action:${actionSlug}` : '';
    const label = token.label?.trim()
      || (actionSlug
        ? (resolvedAction && resolvedAction !== actionPlaceholder
          ? resolvedAction
          : ruleLabel(options.translateRuleLabel, 'action', 'Action'))
        : inlineRollFormula(token.command)
          || ruleLabel(options.translateRuleLabel, 'roll', 'Roll'));
    const diceClass = !actionSlug && DICE_TEXT_RE.test(label) ? ' orc-dice-token' : '';
    return `<span class="orc-rule-token orc-inline-roll-token${diceClass}" title="${escapeHtml(token.raw)}">${escapeHtml(label)}${punctuationSuffix}</span>`;
  }

  return '';
}

function splitLeadingClosingPunctuation(value: string): { punctuation: string; rest: string } {
  const match = value.match(LEADING_CLOSING_PUNCTUATION_RE);
  return match ? { punctuation: match[1], rest: value.slice(match[1].length) } : { punctuation: '', rest: value };
}

export function renderRichTextMarkup(content: string, options: RenderOptions): string {
  const html = options.html ?? false;
  const inline = options.inline ?? false;
  const tokens = parseRichTextTokens(content);
  return tokens
    .map((token, index) => {
      if (token.kind !== 'text') {
        const next = tokens[index + 1];
        const punctuation = next?.kind === 'text' ? splitLeadingClosingPunctuation(next.value).punctuation : '';
        return renderRuleToken(token, options, punctuation);
      }
      const followsRuleToken = index > 0 && tokens[index - 1].kind !== 'text';
      return renderText(followsRuleToken ? splitLeadingClosingPunctuation(token.value).rest : token.value, html, inline);
    })
    .join('');
}
